import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that detects when an element enters the viewport.
 * SSR-safe — returns isInView=false during server-side rendering.
 *
 * If the element is already in the viewport when mounted, isInView is set
 * synchronously via getBoundingClientRect (no flash of hidden content).
 *
 * @param {Object} options
 * @param {number} options.threshold - Visibility ratio to trigger (0-1). Default 0.15
 * @param {boolean} options.triggerOnce - Only fire once, then disconnect. Default true
 * @param {string} options.rootMargin - Margin around root. Default '0px'
 * @returns {{ ref: React.RefCallback, isInView: boolean }}
 */
export default function useInView({
  threshold = 0.15,
  triggerOnce = true,
  rootMargin = '0px',
} = {}) {
  const [isInView, setIsInView] = useState(false);
  const observerRef = useRef(null);
  const elementRef = useRef(null);
  const alreadyTriggered = useRef(false);

  const ref = useCallback(
    (node) => {
      // Cleanup previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      elementRef.current = node;

      // SSR safety check
      if (typeof window === 'undefined' || !node) return;

      // Already triggered once — don't re-observe
      if (alreadyTriggered.current && triggerOnce) return;

      // Respect prefers-reduced-motion
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      if (prefersReducedMotion) {
        setIsInView(true);
        alreadyTriggered.current = true;
        return;
      }

      // Synchronous check: if element is already in the viewport, mark
      // visible immediately so there's no flash of hidden content.
      const rect = node.getBoundingClientRect();
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleRatio = Math.max(0,
        Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0)
      ) / rect.height;

      if (rect.height > 0 && visibleRatio >= threshold) {
        setIsInView(true);
        alreadyTriggered.current = true;
        if (triggerOnce) return; // no need for observer
      }

      // Set up observer for elements not yet in view (or non-triggerOnce)
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            alreadyTriggered.current = true;
            if (triggerOnce) {
              observer.disconnect();
            }
          } else if (!triggerOnce) {
            setIsInView(false);
          }
        },
        { threshold, rootMargin }
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [threshold, triggerOnce, rootMargin]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { ref, isInView };
}
